import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";

const contacts = [
  { icon: "ri-mail-line", label: "商务邮箱", value: "hi@xiaomitao.com", copyable: true },
  { icon: "ri-customer-service-line", label: "合作热线", value: "400-000-2026", copyable: true },
  { icon: "ri-wechat-line", label: "微信公众号", value: "小蜜桃PeachMedia", copyable: true },
  { icon: "ri-weibo-line", label: "官方微博", value: "@小蜜桃娱乐", copyable: false },
];

const stats = [
  { value: "120万+", label: "月活跃读者" },
  { value: "4 大", label: "内容频道" },
  { value: "3600+", label: "收录内容" },
  { value: "6 年", label: "持续运营" },
];

const faqs = [
  {
    q: "小蜜桃主要提供哪些内容？",
    a: "小蜜桃聚焦影视、音乐、生活方式与品牌四大内容频道，涵盖影视资讯、音乐推荐、生活灵感以及品牌动态，帮助用户一站式获取多元娱乐内容。",
  },
  {
    q: "网站上的内容可以转载吗？",
    a: "本站内容以演示数据为主，仅供浏览参考。如需内容合作或转载授权，欢迎通过商务邮箱与我们联系。",
  },
  {
    q: "如何投稿或与我们合作？",
    a: "我们欢迎创作者与品牌投稿和共创。你可以前往「品牌服务」页填写咨询表单，或直接发送邮件至商务邮箱，我们会尽快回复。",
  },
  {
    q: "为什么有的内容看起来是示范数据？",
    a: "当前网站为演示版本，影视、音乐等资讯与品牌案例均为虚构的示范内容，用于展示页面结构与阅读体验，不代表真实报道。",
  },
];

export default function AboutPage() {
  const [toast, setToast] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageMeta({
    title: "小蜜桃 - 关于我们与联系方式",
    description:
      "了解小蜜桃的网站定位与发展，获取商务邮箱、合作热线等联系方式，并查看常见问题解答。",
    keywords: "小蜜桃,关于我们,联系方式,娱乐媒体,常见问题",
  });

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(`已复制：${text}`);
    } catch {
      setToast(`复制失败，请手动复制：${text}`);
    }
    setTimeout(() => setToast(""), 2200);
  };

  return (
    <SiteLayout>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <span className="inline-flex items-center gap-2 text-xs text-primary-700 bg-primary-50 px-3 py-1 rounded-full">
            <i className="ri-plant-line"></i>
            关于小蜜桃
          </span>
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight max-w-3xl">
            做一家亲切、真诚、有趣的娱乐媒体
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
            小蜜桃成立于 2020 年，是一个聚焦影视、音乐、生活方式与品牌服务的综合娱乐媒体平台。
            我们希望用更轻松的方式，把值得被看见的内容和故事，讲给每一个愿意停下来读一读的人。
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5"
              >
                <p className="font-heading font-bold text-xl md:text-2xl text-primary-600">
                  {s.value}
                </p>
                <p className="mt-1 text-xs md:text-sm text-foreground-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              我们在做什么
            </h2>
            <div className="mt-5 flex flex-col gap-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                在信息过载的时代，我们不追求把最多的声音塞给你，而是希望每一篇内容都值得你花时间。
                因此，小蜜桃的内容团队坚持两个原则：一是<strong className="text-foreground-950">真实</strong>，
                不为了流量而夸大；二是<strong className="text-foreground-950">有用</strong>，
                让读者读完之后，能带走一点情绪或一点信息。
              </p>
              <p>
                目前小蜜桃设有影视、音乐、生活方式与品牌四大频道。影视频道追踪院线与剧集动态，
                音乐频道每周更新值得循环的歌单，生活方式频道分享让日常更舒服的小事，
                品牌频道则记录我们与伙伴们一起完成的有趣项目。
              </p>
              <p>
                未来，我们会继续深耕内容本身，也期待和更多创作者、品牌与机构一起，
                把「小蜜桃」做成一个让人愿意常回来看看的地方。
              </p>
            </div>

            <div className="mt-8 w-full h-56 md:h-72 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=bright%20modern%20editorial%20office%20space%2C%20warm%20natural%20light%2C%20cream%20and%20orange%20tones%2C%20plants%20and%20bookshelves%2C%20people%20working%20casually%2C%20clean%20lifestyle%20photography%2C%20high%20detail&width=1200&height=700&seq=xmt-about-team&orientation=landscape"
                alt="小蜜桃内容团队办公场景"
                title="小蜜桃团队 - 综合娱乐媒体"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              联系方式
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              点击即可复制，邮件地址也可直接在邮件客户端打开。
            </p>

            <div className="mt-5 flex flex-col gap-3">
              {contacts.map((c) =>
                c.copyable ? (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => copy(c.value)}
                    className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-4 text-left hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs text-foreground-500">{c.label}</span>
                      <span className="block text-sm text-foreground-900 truncate">
                        {c.value}
                      </span>
                    </span>
                    <i className="ri-file-copy-line text-foreground-400"></i>
                  </button>
                ) : (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-4"
                  >
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-secondary-50 text-secondary-700 flex items-center justify-center">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs text-foreground-500">{c.label}</span>
                      <span className="block text-sm text-foreground-900 truncate">
                        {c.value}
                      </span>
                    </span>
                  </div>
                )
              )}
            </div>

            <a
              href="mailto:hi@xiaomitao.com"
              className="mt-4 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              发送邮件给我们
            </a>

            <div className="mt-6 bg-primary-50 border border-primary-100 rounded-lg p-5">
              <p className="text-sm text-foreground-700 leading-relaxed">
                想聊合作？前往品牌服务页填写咨询表单，我们会更快地收到你的需求。
              </p>
              <Link
                to="/brand"
                className="mt-3 inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                前往品牌服务
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-12 md:pb-16">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          常见问题
        </h2>
        <div className="mt-6 max-w-3xl flex flex-col gap-3">
          {faqs.map((faq, idx) => {
            const open = openFaq === idx;
            return (
              <div
                key={faq.q}
                className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 p-4 md:p-5 text-left cursor-pointer"
                >
                  <span className="font-medium text-sm md:text-base text-foreground-950">
                    {faq.q}
                  </span>
                  <i
                    className={`shrink-0 text-foreground-500 transition-transform ${
                      open ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </button>
                {open && (
                  <p className="px-4 md:px-5 pb-5 -mt-1 text-sm text-foreground-600 leading-relaxed animate-fade-in">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-foreground-950 text-background-50 text-sm px-4 py-2.5 rounded-full animate-scale-in">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}